import { RouterState } from 'connected-react-router';
import { ErrorCodeEnum } from '../constants/errors';
import { ALLOWED_RECIPIENT_UID, KEY, ONE_LINK_ID, ORDER_ID } from '../constants/shipping';
import {
  IAddressInfo,
  IContactInfo,
  ICustomItemsInformation,
  ICustomizableStoredItem,
  IExperience,
  IMskuInformation,
  IOrgInfo,
  IPickedMskuItem,
  IPYGInformation,
  IReceiverInfo,
  ISenderInfo,
  IWhiteLabeling,
  TInitAddress,
} from './api';
import {
  IGiftBudget,
  IMarketplaceCartLine,
  IMarketplaceCatalogContext,
  IMarketplaceCatalogItem,
  IMarketplaceCategory,
  IMarketplaceFilters,
  IMarketplaceProductDetails,
  MarketplaceDetailsStatusEnum,
  TMarketplaceSubmitLine,
} from './marketplace';
import { ICampaign, ICampaignItem } from './pyg';
import { IShippingRules } from './shell';
import { IInitKeys, IPickAddressForm } from './shipping';

export enum ActionsEnum {
  InitApp = 'init_app',
  SetKeys = 'set_keys',
  UpdateKeys = 'update_keys',
  ClearKeys = 'clear_keys',

  GetFormDataRequest = 'get_form_data_request',
  GetFormDataFailure = 'get_form_data_failure',
  GetFormDataSuccess = 'get_form_data_success',

  UpdateRequest = 'update_request',
  UpdateFailure = 'update_failure',
  UpdateSuccess = 'update_success',

  SubmitPYG = 'submit_pick_your_gift',
  SubmitKit = 'submit_kit_choice',
  SubmitMSKU = 'submit_pick_items',

  SetPickedCampaignId = 'set_picked_campaign_id',
  SetCustomizableItems = 'set_customizable_items',
  SubmitCustomizedItems = 'submit_customized_items',

  SetIsFormSubmitted = 'set_is_form_submitted',
  SetPassword = 'set_password',
  SetWhiteLabeling = 'set_white_labeling',
  Set2FATo = 'set_2fa_to',
  Set2FACode = 'set_2fa_code',

  AuthorizeReceiverRequest = 'authorize_receiver_request',

  LoadIframeInitData = 'load_iframe_init_data',
  SetIframeExperience = 'set_iframe_experience',

  MarketplaceCatalogRequest = 'marketplace_catalog_request',
  MarketplaceCatalogSuccess = 'marketplace_catalog_success',
  MarketplaceCatalogFailure = 'marketplace_catalog_failure',
  MarketplaceCategoriesSuccess = 'marketplace_categories_success',
  MarketplaceSetFilters = 'marketplace_set_filters',
  MarketplaceProductRequest = 'marketplace_product_request',
  MarketplaceProductSuccess = 'marketplace_product_success',
  MarketplaceProductFailure = 'marketplace_product_failure',
  MarketplaceHydrateItemsRequest = 'marketplace_hydrate_items_request',
  MarketplaceHydrateItemSuccess = 'marketplace_hydrate_item_success',
  MarketplaceHydrateItemFailure = 'marketplace_hydrate_item_failure',
  MarketplaceAddDirectRequest = 'marketplace_add_direct_request',
  MarketplaceOpenDetails = 'marketplace_open_details',
  MarketplaceCloseDetails = 'marketplace_close_details',
  MarketplaceSetCartOpen = 'marketplace_set_cart_open',
  MarketplaceSwapStart = 'marketplace_swap_start',
  MarketplaceSwapCancel = 'marketplace_swap_cancel',
  MarketplaceAddToCart = 'marketplace_add_to_cart',
  MarketplaceRemoveFromCart = 'marketplace_remove_from_cart',
  MarketplaceClearCart = 'marketplace_clear_cart',
  MarketplaceReplaceCart = 'marketplace_replace_cart',
  MarketplaceRestoreCart = 'marketplace_restore_cart',
  MarketplaceDropReplacedCart = 'marketplace_drop_replaced_cart',
  MarketplaceSetSupplierFilter = 'marketplace_set_supplier_filter',
  MarketplaceSetCacheValue = 'marketplace_set_cache_value',
}

export interface IAction<P = unknown, M = null> {
  type: ActionsEnum;
  payload?: P;
  meta?: M;
}

export interface IError {
  title?: string;
  message?: string;
  error_code?: ErrorCodeEnum;
}

export interface IKeysState extends IInitKeys {
  isInitialized: boolean;
}

export interface IInitState {
  isLoading: boolean;
  error: IError | null;
  org: IOrgInfo | null;
  whiteLabeling: IWhiteLabeling | null;
  sender: ISenderInfo | null;
  address: TInitAddress | null;
  campaigns: ICampaign[] | null;
  items: ICampaignItem[] | null;
  isDigital: boolean;
  isFormSubmitted: boolean;
  password: string | null;
  send2FATo: string | null;
  code: string | null;
  shippingRules: IShippingRules | null;
  experience: IExperience | null;
  isEmbed: boolean;
  giftBudget: IGiftBudget | null;
  customScenarios: string[];
}

export interface IFormState {
  address: IReceiverInfo | null;
  pickedCampaignId: string | null;
  pickedItems: { [id: string]: IPickedMskuItem } | null;
  customItems: { [id: string]: ICustomizableStoredItem } | null;
}

export interface IAppState {
  keys: IKeysState;
  init: IInitState;
  form: IFormState;
  marketplace: IMarketplaceState;
  router: RouterState;
  shell: IShellState;
}
export interface IShellState {
  build: string | undefined;
  commit: string | undefined;
}

/* Action interfaces */
export interface ISetKeysPayload {
  [ORDER_ID]?: string;
  [KEY]?: string;
  [ONE_LINK_ID]?: string;
  [ALLOWED_RECIPIENT_UID]?: string;
  resolve?: (value: PromiseLike<unknown> | unknown) => void;
  reject?: (e?: unknown) => void;
}

export interface ICustomScenario {
  // EX-5870: opaque per-claim token stored on the One Link document. Never a One Link id, so the
  // bundle cannot be used to enumerate which links carry a custom scenario.
  value: string;
}

export interface IInitSuccessPayload {
  org_info: IOrgInfo;
  sender_info: ISenderInfo;
  receiver_info: TInitAddress;
  ds_customized_texts: IWhiteLabeling;
  pick_campaigns: null | ICampaign[];
  inventory_items: null | ICampaignItem[];
  is_digital?: boolean;
  shipping_rules: IShippingRules;
  experience?: IExperience;
  // EX-6398: present only when the send carries a gift budget. No dollar amount is ever sent
  // to the claim page.
  gift_budget?: IGiftBudget;
  // EX-5870: present on both the One Link and the send init responses, and only for claims whose
  // document is set up with one. The humanized label stays in the Portal contract.
  custom_scenarios?: ICustomScenario[];
}

export interface IMarketplaceState {
  items: IMarketplaceCatalogItem[];
  categories: IMarketplaceCategory[] | null;
  filters: IMarketplaceFilters;
  nextCursor: string | null;
  cursorBudget: number | null;
  catalogContext: IMarketplaceCatalogContext | null;
  isLoading: boolean;
  isLoadingMore: boolean;
  error: IError | null;
  details: { [productId: string]: IMarketplaceProductDetails };
  // Keyed by bare product id, never internalId: formatSkuOptions regenerates internalId on every
  // init and campaign pick. This is also what tells "not fetched yet" apart from "fetched, one
  // variant" — both of which leave the item without sku_options.
  detailsStatus: { [productId: string]: MarketplaceDetailsStatusEnum };
  cart: IMarketplaceCartLine[];
  // Product whose details sidebar is open. In the store rather than the page because the
  // direct-add saga opens it: it is the one that learns a product has variants to pick.
  openProductId: string | null;
  // Picks drawer; in the store because starting a swap opens it.
  isCartOpen: boolean;
  // Pending swap: the cart line waiting for room. Removing picks re-evaluates it, and it is
  // added automatically as soon as it fits.
  swapPending: IMarketplaceCartLine | null;
  // Cart as it was before the last supplier replace, while its Undo is on offer. Any other cart
  // change drops it, so Undo can never wipe a newer pick.
  replacedCart: IMarketplaceCartLine[] | null;
  // Narrows the loaded grid to the locked supplier. Client-side: the catalog endpoint has no
  // supplier filter, so toggling it never refetches.
  isSupplierFilterOn: boolean;
  // Opaque server token echoed as X-Marketplace-Cache. Per claim: the slice resets on init and is
  // not persisted.
  cacheValue: string | null;
}

export interface IMarketplaceHydrateItemsPayload {
  productIds: string[];
}

export interface IMarketplaceHydrateItemSuccessPayload {
  productId: string;
  details: IMarketplaceProductDetails;
}

export interface IMarketplaceHydrateItemFailurePayload {
  productId: string;
  error?: IError;
}

export interface IMarketplaceCatalogRequestPayload {
  isLoadMore?: boolean;
}

export interface IMarketplaceCatalogSuccessPayload {
  items: IMarketplaceCatalogItem[];
  nextCursor: string | null;
  budget: number | null;
  isLoadMore?: boolean;
}

export interface ISetWhiteLabelingPayload {
  org_info: IOrgInfo;
  sender_info: ISenderInfo;
  ds_customized_texts: IWhiteLabeling;
}

export interface ISubmitPYGPayload {
  campaignId: string;
  resolve?: (route: string) => void;
}

export type TSubmitKitPayload = Pick<ISubmitPYGPayload, 'resolve'>;

export interface ISubmitCustomItemsPayload {
  [internalId: string]: string;
}

export interface ISubmitMSKUPayload {
  pickedItems: { [p: string]: IPickedMskuItem };
  resolve?: (route: string) => void;
}

export type TShippingStepResultValues = IContactInfo & (IAddressInfo | IPickAddressForm);

export interface ISubmitAddressPayload {
  values: TShippingStepResultValues;
  resolve?: (value: PromiseLike<unknown> | unknown) => void;
  reject?: (e?: unknown) => void;
}

export interface IAuthorizeReceiverPayload {
  action: AuthActionEnum;
  to: string | null;
  code?: string;
  one_link_id?: string;
  id?: string;
  resolve?: (value: PromiseLike<unknown> | unknown) => void;
  reject?: (e?: unknown) => void;
}

export interface IAuthorizeReceiverSuccessPayload {
  id: string;
}

export enum AuthActionEnum {
  Init = 'init',
  Verify = 'verify',
}

// picked_items is the wire shape (array, from preparePickedItemsForAPI), not the store map.
export type TUpdateRequestBody = Omit<
  Partial<IReceiverInfo & IInitKeys & IPYGInformation & IMskuInformation & ICustomItemsInformation>,
  'picked_items'
> & {
  picked_items?: IPickedMskuItem[];
  // Budget path only: present iff pre_created_engagement_id and picked_items are both absent.
  marketplace?: TMarketplaceSubmitLine[];
};
